/**
 * InstallNudgeBanner — Iter268
 *
 * Slim promotional banner that surfaces the PWA install prompt on the
 * SmithDashboard. Visibility rules:
 *   • Hidden when the app is already installed (display-mode:standalone)
 *   • Hidden until the user has logged in ≥ 2 times (login_count in
 *     localStorage — bumped by AuthContext.login/register/2fa)
 *   • Once dismissed → silenced for 30 days (`agentelead.install_nudge_dismissed_at`)
 *
 * Renders nothing when hidden. Never blocks the UI.
 */
import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import InstallPromptButton from "./InstallPromptButton";

const LOGIN_MIN = 2;
const SILENCE_DAYS = 30;

function _isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
}

export default function InstallNudgeBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (_isStandalone()) return;
    try {
      const count = parseInt(window.localStorage.getItem("agentelead.login_count") || "0", 10);
      if (count < LOGIN_MIN) return;
      const dismissedAt = parseInt(
        window.localStorage.getItem("agentelead.install_nudge_dismissed_at") || "0", 10);
      const daysAgo = (Date.now() - dismissedAt) / (24 * 3600 * 1000);
      if (dismissedAt && daysAgo < SILENCE_DAYS) return;
      setVisible(true);
    } catch (_e) { /* ignore */ }
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    try {
      window.localStorage.setItem(
        "agentelead.install_nudge_dismissed_at", String(Date.now()));
    } catch (_e) { /* ignore */ }
    setVisible(false);
  };

  return (
    <div className="rounded-2xl border p-3 sm:p-4 flex items-center gap-3 sm:gap-4"
         data-testid="install-nudge-banner"
         style={{
           background: "linear-gradient(90deg,rgba(201,162,78,0.16) 0%,rgba(10,18,51,0.85) 60%)",
           borderColor: "rgba(201,162,78,0.45)",
         }}>
      <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center shrink-0"
           style={{ background: "linear-gradient(135deg,#C9A24E 0%,#8A6A2A 100%)" }}>
        <Download className="w-5 h-5" style={{ color: "#0A1233" }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] sm:text-[14px] font-black text-white truncate">
          Instale o AgenteLead na tela inicial
        </div>
        <div className="text-[11.5px] sm:text-[12.5px] text-slate-300 truncate">
          1-toque em qualquer lead · notificações de foreclosure sem abrir o browser · offline básico
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <InstallPromptButton variant="chip" />
        <button type="button" onClick={dismiss}
                data-testid="install-nudge-dismiss"
                className="w-9 h-9 sm:w-8 sm:h-8 rounded-lg hover:bg-white/10 text-slate-400 flex items-center justify-center touch-target"
                title="Adiar por 30 dias">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
